import React from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import Card from 'components/Card';
import Disclosure from 'components/Disclosure';
import Button from 'components/Buttons/Button';
import { CustomFieldDefinitionWithTargets } from 'types/customField';
import { v4 as uuid } from 'uuid';
import { Schoolyear } from 'types/schoolyear';
import { School } from 'types/school';
import { CustomFieldDefinitionComponent } from './CustomField';
import { useEventCustomFieldDefinitions } from 'queries/events/customfields/useEventCustomFieldDefinitions';
import { useCreateEventCustomFieldDefinition } from 'queries/events/customfields/useCreateEventCustomFieldDefinition';
import { useUpdateEventCustomFieldDefinition } from 'queries/events/customfields/useUpdateEventCustomFieldDefinition';
import { useDeleteEventCustomFieldDefinition } from 'queries/events/customfields/useDeleteEventCustomFieldDefinition';
import { useUpdateSchoolEventCustomFieldDefinition } from 'queries/events/customfields/useUpdateSchoolEventCustomFieldDefinition';
import { useReorderEventCustomFieldDefinitions } from 'queries/events/customfields/useReorderEventCustomFieldDefinitions';
import { CustomFieldSkeletons } from './CustomFieldSkeleton';
import { PlusIcon } from '@heroicons/react/24/outline';
import swap from 'utils/arrays/swap';

interface BodyProps {
  schoolyearId: string;
  schoolId: string;
}

function Body({ schoolyearId, schoolId }: BodyProps) {
  const { t } = useTranslation(['userSettings']);
  const params = { schoolyearId, schoolId };
  const { data: customFieldDefinitions = [], isLoading } =
    useEventCustomFieldDefinitions(params);
  const { mutateAsync: updateField } =
    useUpdateEventCustomFieldDefinition(params);
  const { mutateAsync: deleteField } =
    useDeleteEventCustomFieldDefinition(params);
  const { mutateAsync: updateSchoolEventCustomField } =
    useUpdateSchoolEventCustomFieldDefinition(params);
  const { mutateAsync: reorderCustomFields } =
    useReorderEventCustomFieldDefinitions(params);

  const handleReorder = (index: number, diff: -1 | 1) => {
    const newOrderIds = swap(
      customFieldDefinitions.map(f => f.id),
      index,
      diff,
    );

    reorderCustomFields(newOrderIds);
  };

  if (isLoading) {
    return <CustomFieldSkeletons />;
  }

  if (customFieldDefinitions.length === 0) {
    return (
      <Card tw="mb-3 relative">
        <div tw="text-muted text-sm">
          {t('userSettings:customFields.noCustomFieldForSchool')}
        </div>
      </Card>
    );
  }

  return (
    <div tw="flex flex-col gap-y-4">
      {customFieldDefinitions.map((def, index) => (
        <Card tw="mb-3 relative" key={def.id}>
          <CustomFieldDefinitionComponent
            item={def}
            onChange={updateField}
            onDelete={() => deleteField(def.id)}
            availableTypes={['text', 'select', 'multiselect']} // TODO : enable more types
            availableTargets={['cahierJournal', 'headCahierJournal']}
            definitionTargets={def.targets}
            onTargetToggle={target =>
              updateSchoolEventCustomField({
                customFieldId: def.id,
                body: {
                  targets: def.targets.includes(target)
                    ? def.targets.filter(v => v !== target)
                    : [...def.targets, target],
                },
              })
            }
            isFirstItem={index === 0}
            isLastItem={index === customFieldDefinitions.length - 1}
            onReorder={diff => handleReorder(index, diff)}
          />
        </Card>
      ))}
    </div>
  );
}

interface Props {
  schoolyear: Schoolyear;
  school: School;
}

export function EventCustomFields({ schoolyear, school }: Props) {
  const { t } = useTranslation(['userSettings']);
  const { mutateAsync: createField } = useCreateEventCustomFieldDefinition({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });

  return (
    <Disclosure fill>
      <Disclosure.Button
        minimal
        fill
        large
        iconPosition="left"
        alignText="left"
        iconRotation="bottom-to-top"
        tw="mb-3"
      >
        <div tw="flex justify-between flex-grow">
          <div>
            <div tw="font-medium truncate">{school.name}</div>
            <span tw="text-muted text-sm">
              {(school.classrooms || []).map(c => c.name).join(' · ')}
            </span>
          </div>
          <div tw="text-muted text-sm">{schoolyear.label}</div>
        </div>
      </Disclosure.Button>
      <Disclosure.Panel>
        <Body schoolyearId={schoolyear.id} schoolId={school.id} />
        <div tw="text-right mb-3">
          <Button
            type="button"
            intent="primary"
            small
            onClick={() => {
              const newCF: CustomFieldDefinitionWithTargets = {
                id: uuid(),
                name: '',
                type: 'text',
                options: [],
                targets: ['cahierJournal'],
              };
              return createField(newCF);
            }}
            icon={PlusIcon}
          >
            {t('userSettings:customFields.addCustomFieldForSchool')}
          </Button>
        </div>
      </Disclosure.Panel>
    </Disclosure>
  );
}
